// ===========================================
// COMPONENTE: Badge de Notificaciones
// ===========================================

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { NotificacionItem } from './NotificacionItem'
import {
  useNotificacionesNoLeidas,
  useConteoNotificaciones,
  useMarcarLeida,
  useMarcarTodasLeidas,
} from '@/core/hooks/useNotificaciones'
import { Loader2, CheckCheck, BellOff } from 'lucide-react'

export function NotificacionBadge() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const { data: notificaciones = [], isLoading } = useNotificacionesNoLeidas(10)
  const { data: conteo = 0 } = useConteoNotificaciones()
  const marcarLeida = useMarcarLeida()
  const marcarTodasLeidas = useMarcarTodasLeidas()

  const handleMarcarLeida = (id: string) => {
    marcarLeida.mutate(id)
  }

  const handleMarcarTodasLeidas = () => {
    marcarTodasLeidas.mutate()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {conteo > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
              {conteo > 9 ? '9+' : conteo}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[380px] max-w-[calc(100vw-2rem)] p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold">Notificaciones</h4>
          {conteo > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarcarTodasLeidas}
              disabled={marcarTodasLeidas.isPending}
            >
              {marcarTodasLeidas.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCheck className="h-4 w-4 mr-1" />
                  Marcar todas
                </>
              )}
            </Button>
          )}
        </div>

        {/* Lista de notificaciones */}
        <ScrollArea className="h-[400px] max-h-[calc(100vh-12rem)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : notificaciones.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <BellOff className="h-8 w-8 mb-2" />
              <p className="text-sm">No hay notificaciones</p>
            </div>
          ) : (
            <div className="divide-y">
              {notificaciones.map((notificacion) => (
                <NotificacionItem
                  key={notificacion.id}
                  notificacion={notificacion}
                  onMarcarLeida={handleMarcarLeida}
                  compact
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notificaciones.length > 0 && (
          <>
            <Separator />
            <div className="p-2 text-center">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={() => {
                  setOpen(false)
                  navigate('/notificaciones')
                }}
              >
                Ver todas las notificaciones
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
